import { useCallback, useMemo } from 'react';

import {
  QuickActionDto,
  useSnoozeQuickAction,
  usePatchQuickAction,
  useListQuickActions,
  useGetNextQuickActions,
  getListQuickActionsQueryKey
} from '@/api/generated';

export const generateListQuickActionsQueryKey = (count: number) =>
  getListQuickActionsQueryKey() + `?count=${count}`;

export default function useQuickActions({ count }: { count: number }) {
  const queryKey = generateListQuickActionsQueryKey(count);

  const {
    data: quickActionsResponse,
    refetch: refetchQuickActions,
    isLoading: isLoadingQuickActions,
    isError: isErrorQuickActions
  } = useListQuickActions({ query: { queryKey: [queryKey] } });

  const quickActions = useMemo(
    () => quickActionsResponse?.items || [],
    [quickActionsResponse?.items]
  );

  const { mutateAsync: mutateSnoozeQuickAction, isError: isErrorSnoozeQuickAction } =
    useSnoozeQuickAction();
  const { mutateAsync: mutatePatchQuickAction, isError: isErrorPatchQuickAction } =
    usePatchQuickAction();

  const snoozeQuickAction = useCallback(
    async (quickActionId: string) => {
      await mutateSnoozeQuickAction({ id: quickActionId });
    },
    [mutateSnoozeQuickAction]
  );

  const patchQuickAction = useCallback(
    async (quickActionId: string, quickAction: QuickActionDto) => {
      await mutatePatchQuickAction({ id: quickActionId, data: quickAction });
    },
    [mutatePatchQuickAction]
  );

  const { refetch: refetchNextQuickAction } = useGetNextQuickActions(
    { count },
    {
      query: {
        queryKey: ['nextQuickAction'],
        enabled: false
      }
    }
  );

  return {
    snoozeQuickAction,
    patchQuickAction,
    isErrorSnoozeQuickAction,
    isErrorPatchQuickAction,
    quickActions,
    refetchQuickActions,
    isLoadingQuickActions,
    isErrorQuickActions,
    refetchNextQuickAction
  };
}
